import useFetch from "hooks/useFetch";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Column,
  EmptyState,
  Icon,
  PageHeader,
  Select,
  Skeleton,
  Table,
  Textarea,
  TextField,
  useConfirm,
} from "stories/Admin";
import PartnerKeyCard from "pages/PartnerPage/PartnerKeyCard";
import {
  formatDateTime,
  formatDay,
  formatNumber,
  isoDayFromToday,
  PartnerCreditCheck,
  PartnerKey,
  PartnerOrg,
  PartnerStatus,
  partnerStatusLabel,
} from "pages/PartnerPage/partnerTypes";
import styles from "pages/PartnerPage/PartnerPage.module.scss";
import PageModal from "../LocationsPage/PageModal";

/*
 * Superadmin: partnere som viser iBønn-widgeten på egen nettside. Opprett partneren, gi admin,
 * og se nøkler, domener, bruk og krediteringssjekk. org.nr. og notat vises bare her.
 * Superadmin kan også lage og endre nøkler for partneren, og opprette en moské som partner.
 */

interface MosqueOption {
  orgNr: string;
  orgName: string;
  name?: string;
}

interface KeyForm {
  orgId: number;
  key?: PartnerKey;
  name: string;
  pageUrl: string;
}

interface OrgForm {
  id?: number;
  name: string;
  orgNr: string;
  contactName: string;
  contactEmail: string;
  note: string;
  status: PartnerStatus;
  maxKeys: string;
}

const emptyForm: OrgForm = {
  name: "",
  orgNr: "",
  contactName: "",
  contactEmail: "",
  note: "",
  status: "ACTIVE",
  maxKeys: "3",
};

const PartnersPage: React.FC = () => {
  const { execute, lastError } = useFetch<any>();
  const executeRef = useRef(execute);
  executeRef.current = execute;
  const lastErrorRef = useRef(lastError);
  lastErrorRef.current = lastError;
  const confirm = useConfirm();

  const [orgs, setOrgs] = useState<PartnerOrg[] | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [form, setForm] = useState<OrgForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [adminEmail, setAdminEmail] = useState("");
  const [adminError, setAdminError] = useState<string | null>(null);
  const [checkingId, setCheckingId] = useState<number | null>(null);
  const [notice, setNotice] = useState<{
    tone: "ok" | "warn" | "error";
    text: string;
  } | null>(null);
  const [history, setHistory] = useState<{
    key: PartnerKey;
    rows: PartnerCreditCheck[] | null;
  } | null>(null);
  const [showRevoked, setShowRevoked] = useState(false);
  const [keyForm, setKeyForm] = useState<KeyForm | null>(null);
  const [keyError, setKeyError] = useState<string | null>(null);
  const [mosques, setMosques] = useState<MosqueOption[] | null>(null);
  const [activateForm, setActivateForm] = useState<{
    key: PartnerKey;
    graceUntil: string;
  } | null>(null);
  const [activateError, setActivateError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const result = await executeRef.current("/api/v1/admin/partners", "GET");
    setOrgs(Array.isArray(result) ? result : []);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const selected = useMemo(
    () => orgs?.find((o) => o.id === selectedId) ?? null,
    [orgs, selectedId],
  );

  const saveOrg = async () => {
    if (!form) return;
    setSaving(true);
    setFormError(null);
    const body = {
      name: form.name.trim(),
      orgNr: form.orgNr.trim(),
      contactName: form.contactName.trim(),
      contactEmail: form.contactEmail.trim(),
      note: form.note.trim(),
      status: form.status,
      maxKeys: Number(form.maxKeys) || 3,
    };
    const result = form.id
      ? await executeRef.current(
          `/api/v1/admin/partners/${form.id}`,
          "PATCH",
          body,
        )
      : await executeRef.current("/api/v1/admin/partners", "POST", body);
    setSaving(false);
    if (result) {
      setForm(null);
      setSelectedId(result.id);
      load();
    } else {
      setFormError(lastErrorRef.current()?.message || "Kunne ikke lagre.");
    }
  };

  const openNewOrg = async () => {
    setForm({ ...emptyForm });
    setFormError(null);
    if (mosques === null) {
      const result = await executeRef.current("/api/v1/mosques", "GET");
      setMosques(
        (Array.isArray(result) ? (result as MosqueOption[]) : [])
          .filter((m) => m.orgNr)
          .sort((a, b) =>
            (a.name || a.orgName).localeCompare(b.name || b.orgName, "nb"),
          ),
      );
    }
  };

  const fillFromMosque = (orgNr: string) => {
    const m = mosques?.find((x) => x.orgNr === orgNr);
    if (!m) return;
    set({ name: m.name || m.orgName, orgNr: m.orgNr });
  };

  const saveKey = async () => {
    if (!keyForm) return;
    setSaving(true);
    setKeyError(null);
    const body = {
      name: keyForm.name.trim() || undefined,
      pageUrl: keyForm.pageUrl.trim(),
    };
    const result = keyForm.key
      ? await executeRef.current(
          `/api/v1/partners/${keyForm.orgId}/keys/${keyForm.key.id}`,
          "PATCH",
          body,
        )
      : await executeRef.current(
          `/api/v1/partners/${keyForm.orgId}/keys`,
          "POST",
          body,
        );
    setSaving(false);
    if (result) {
      setNotice({
        tone: "ok",
        text: keyForm.key
          ? "Lagret."
          : `Nøkkelen for ${result.domain} er laget. Send koden til partneren, og trykk «Sjekk nå» når den er lagt inn.`,
      });
      setKeyForm(null);
      load();
    } else {
      setKeyError(lastErrorRef.current()?.message || "Kunne ikke lagre.");
    }
  };

  const grantAdmin = async () => {
    if (!selected) return;
    setAdminError(null);
    const result = await executeRef.current(
      `/api/v1/admin/partners/${selected.id}/admins`,
      "POST",
      {
        email: adminEmail.trim(),
      },
    );
    if (result) {
      setAdminEmail("");
      load();
    } else {
      setAdminError(lastErrorRef.current()?.message || "Kunne ikke gi admin.");
    }
  };

  const revokeAdmin = async (org: PartnerOrg, id: number, email: string) => {
    const ok = await confirm({
      title: "Fjerne admin?",
      message: `${email || "Brukeren"} kan ikke lenger lage nøkler for ${org.name}.`,
      confirmLabel: "Fjern",
      tone: "danger",
    });
    if (!ok) return;
    await executeRef.current(
      `/api/v1/admin/partners/${org.id}/admins/${id}`,
      "DELETE",
    );
    load();
  };

  const check = async (key: PartnerKey) => {
    setCheckingId(key.id);
    setNotice(null);
    const result = await executeRef.current(
      `/api/v1/admin/partner-keys/${key.id}/check`,
      "POST",
    );
    setCheckingId(null);
    setNotice(
      result
        ? {
            tone: result.result === "OK" ? "ok" : "warn",
            text: `${key.domain}: ${result.message || result.result}`,
          }
        : { tone: "error", text: "Kunne ikke sjekke siden." },
    );
    load();
  };

  const revokeKey = async (key: PartnerKey) => {
    const ok = await confirm({
      title: `Trekke tilbake «${key.name}»?`,
      message: `Widgeten på ${key.domain} slutter å virke med en gang.`,
      confirmLabel: "Trekk tilbake",
      tone: "danger",
    });
    if (!ok) return;
    await executeRef.current(`/api/v1/admin/partner-keys/${key.id}`, "DELETE");
    load();
  };

  const openActivate = (key: PartnerKey) => {
    setActivateError(null);
    setActivateForm({
      key,
      graceUntil:
        key.graceUntil && key.graceUntil >= isoDayFromToday(0)
          ? key.graceUntil
          : isoDayFromToday(14),
    });
  };

  const activate = async () => {
    if (!activateForm) return;
    setSaving(true);
    setActivateError(null);
    const result = await executeRef.current(
      `/api/v1/admin/partner-keys/${activateForm.key.id}/activate`,
      "POST",
      { graceUntil: activateForm.graceUntil },
    );
    setSaving(false);
    if (result) {
      setNotice({
        tone: "ok",
        text: `${activateForm.key.domain} er aktivert til og med ${formatDay(activateForm.graceUntil)}.`,
      });
      setActivateForm(null);
      load();
    } else {
      setActivateError(
        lastErrorRef.current()?.message || "Kunne ikke aktivere.",
      );
    }
  };

  const showChecks = async (key: PartnerKey) => {
    setHistory({ key, rows: null });
    const result = await executeRef.current(
      `/api/v1/admin/partner-keys/${key.id}/checks`,
      "GET",
    );
    setHistory({ key, rows: Array.isArray(result) ? result : [] });
  };

  const allKeys = (orgs ?? []).flatMap((o) => o.keys);
  const activeKeys = allKeys.filter((k) => !k.revokedAt);
  const stats: Array<[string, React.ReactNode]> = [
    ["Partnere", orgs ? orgs.filter((o) => o.status !== "CLOSED").length : "–"],
    ["Aktive nøkler", orgs ? activeKeys.length : "–"],
    [
      "Stoppet (kreditering)",
      orgs ? activeKeys.filter((k) => k.pausedAt).length : "–",
    ],
    [
      "Visninger siste 30 dager",
      orgs
        ? formatNumber(allKeys.reduce((s, k) => s + k.usage.last30Days, 0))
        : "–",
    ],
  ];

  const columns: Array<Column<PartnerOrg>> = [
    {
      key: "name",
      header: "Partner",
      render: (o) => (
        <span className={styles.keyTitle}>
          <b>{o.name}</b>
          {o.status !== "ACTIVE" && (
            <Badge tone={o.status === "PAUSED" ? "gold" : "grey"}>
              {partnerStatusLabel[o.status]}
            </Badge>
          )}
        </span>
      ),
    },
    {
      key: "orgNr",
      header: "Org.nr.",
      numeric: true,
      render: (o) => o.orgNr || "–",
    },
    {
      key: "domains",
      header: "Nettsteder",
      render: (o) => {
        const keys = o.keys.filter((k) => !k.revokedAt);
        return keys.length ? (
          keys.map((k) => k.domain).join(", ")
        ) : (
          <span className={styles.muted}>–</span>
        );
      },
    },
    {
      key: "views",
      header: "Visninger 30 d",
      numeric: true,
      align: "right",
      render: (o) =>
        formatNumber(o.keys.reduce((s, k) => s + k.usage.last30Days, 0)),
    },
    {
      key: "credit",
      header: "Kreditering",
      render: (o) => {
        const keys = o.keys.filter((k) => !k.revokedAt);
        if (!keys.length) return <span className={styles.muted}>–</span>;
        const stopped = keys.filter((k) => k.pausedAt).length;
        const ok = keys.filter(
          (k) => k.creditStatus === "OK" && !k.pausedAt,
        ).length;
        return stopped ? (
          <Badge tone="rose">{stopped} stoppet</Badge>
        ) : ok === keys.length ? (
          <Badge tone="mint">OK</Badge>
        ) : (
          <Badge tone="gold">Venter / feil</Badge>
        );
      },
    },
  ];

  const historyColumns: Array<Column<PartnerCreditCheck>> = [
    {
      key: "at",
      header: "Tid",
      numeric: true,
      render: (c) => formatDateTime(c.checkedAt),
    },
    {
      key: "result",
      header: "Resultat",
      render: (c) => (
        <Badge
          tone={
            c.result === "OK" ? "mint" : c.result === "FAILED" ? "rose" : "gold"
          }
        >
          {c.result}
        </Badge>
      ),
    },
    {
      key: "http",
      header: "HTTP",
      numeric: true,
      render: (c) => c.httpStatus ?? "–",
    },
    {
      key: "widget",
      header: "Widget",
      render: (c) => (c.foundWidget ? "Ja" : "Nei"),
    },
    {
      key: "link",
      header: "Lenke",
      render: (c) => (c.foundLink ? "Ja" : "Nei"),
    },
    {
      key: "msg",
      header: "Melding",
      render: (c) => <span className={styles.muted}>{c.message}</span>,
    },
    {
      key: "by",
      header: "Av",
      render: (c) =>
        ({
          NIGHTLY: "Natt",
          ADMIN: "Partner",
          SUPERADMIN: "Superadmin",
          ACTIVATED: "Aktivert",
        })[c.triggeredBy] ?? c.triggeredBy,
    },
  ];

  const set = (patch: Partial<OrgForm>) =>
    setForm((f) => (f ? { ...f, ...patch } : f));

  return (
    <>
      <PageHeader
        title="Partnere"
        subtitle="Organisasjoner som viser dagens bønnetider fra iBønn på egen nettside. Én nøkkel gjelder ett nettsted, og siden sjekkes hver natt for kreditering."
        actions={
          <>
            <Button
              variant="secondary"
              icon={<Icon name="swap" size={16} />}
              onClick={load}
            >
              Oppdater
            </Button>
            <Button icon={<Icon name="plus" size={16} />} onClick={openNewOrg}>
              Ny partner
            </Button>
          </>
        }
      />

      <div className={styles.usageRow}>
        {stats.map(([label, value]) => (
          <div key={label} className={styles.stat}>
            <b>{value}</b>
            <span>{label}</span>
          </div>
        ))}
      </div>

      <Card>
        {orgs === null ? (
          <div className={styles.skeletons}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} height={40} />
            ))}
          </div>
        ) : (
          <Table
            columns={columns}
            rows={orgs}
            rowKey={(o) => o.id}
            onRowClick={(o) => setSelectedId(o.id === selectedId ? null : o.id)}
            rowState={(o) =>
              o.id === selectedId
                ? "on"
                : o.status === "CLOSED"
                  ? "dim"
                  : undefined
            }
            empty={<EmptyState icon="link" title="Ingen partnere ennå" />}
          />
        )}
      </Card>

      {notice && <Alert tone={notice.tone}>{notice.text}</Alert>}

      {selected && (
        <>
          <div className={styles.grid2}>
            <Card
              title={selected.name}
              actions={
                <Button
                  size="sm"
                  variant="secondary"
                  icon={<Icon name="edit" size={14} />}
                  onClick={() => {
                    setFormError(null);
                    setForm({
                      id: selected.id,
                      name: selected.name,
                      orgNr: selected.orgNr ?? "",
                      contactName: selected.contactName ?? "",
                      contactEmail: selected.contactEmail ?? "",
                      note: selected.note ?? "",
                      status: selected.status,
                      maxKeys: String(selected.maxKeys),
                    });
                  }}
                >
                  Endre
                </Button>
              }
            >
              <dl className={styles.facts}>
                <dt>Status</dt>
                <dd>{partnerStatusLabel[selected.status]}</dd>
                <dt>Org.nr.</dt>
                <dd>{selected.orgNr || "–"}</dd>
                <dt>Kontakt</dt>
                <dd>
                  {[selected.contactName, selected.contactEmail]
                    .filter(Boolean)
                    .join(" · ") || "–"}
                </dd>
                <dt>Maks nøkler</dt>
                <dd>{selected.maxKeys}</dd>
                <dt>Opprettet</dt>
                <dd>
                  {formatDateTime(selected.createdAt)}
                  {selected.createdByEmail
                    ? ` av ${selected.createdByEmail}`
                    : ""}
                </dd>
                {selected.note && (
                  <>
                    <dt>Notat</dt>
                    <dd>{selected.note}</dd>
                  </>
                )}
              </dl>
            </Card>

            <Card
              title="Admins"
              subtitle="Kan lage og trekke tilbake nøkler på admin.ibønn.no/partner."
            >
              {(selected.admins ?? []).length === 0 ? (
                <span className={styles.muted}>Ingen admins ennå.</span>
              ) : (
                <ul className={styles.list}>
                  {(selected.admins ?? []).map((a) => (
                    <li key={a.id}>
                      {a.email || a.username || "Ukjent bruker"}{" "}
                      <span className={styles.muted}>
                        gitt {formatDateTime(a.createdAt)}
                        {a.grantedByEmail ? ` av ${a.grantedByEmail}` : ""}
                      </span>{" "}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => revokeAdmin(selected, a.id, a.email)}
                      >
                        Fjern
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
              <div className={styles.adminRow}>
                <TextField
                  label="Gi admin (e-post til en registrert iBønn-bruker)"
                  type="email"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                />
                <Button onClick={grantAdmin} disabled={!adminEmail.trim()}>
                  Gi admin
                </Button>
              </div>
              {adminError && <Alert tone="error">{adminError}</Alert>}
            </Card>
          </div>

          <div className={styles.orgHead}>
            <h2 className={styles.h2}>Nøkler</h2>
            <label className={styles.muted}>
              <input
                type="checkbox"
                checked={showRevoked}
                onChange={(e) => setShowRevoked(e.target.checked)}
              />{" "}
              Vis tilbaketrukne
            </label>
            <Button
              size="sm"
              icon={<Icon name="plus" size={14} />}
              disabled={
                selected.status !== "ACTIVE" ||
                selected.keys.filter((k) => !k.revokedAt).length >=
                  selected.maxKeys
              }
              title={
                selected.status !== "ACTIVE"
                  ? "Partneren må være aktiv"
                  : selected.keys.filter((k) => !k.revokedAt).length >=
                      selected.maxKeys
                    ? `Partneren har ${selected.maxKeys} aktive nøkler (maks)`
                    : undefined
              }
              onClick={() => {
                setKeyError(null);
                setKeyForm({ orgId: selected.id, name: "", pageUrl: "" });
              }}
            >
              Ny nøkkel
            </Button>
          </div>
          {selected.keys.filter((k) => showRevoked || !k.revokedAt).length ===
          0 ? (
            <EmptyState icon="key" title="Ingen nøkler">
              Lag en nøkkel her, eller la partnerens admin lage den selv på
              admin.ibønn.no/partner.
            </EmptyState>
          ) : (
            selected.keys
              .filter((k) => showRevoked || !k.revokedAt)
              .map((k) => (
                <PartnerKeyCard
                  key={k.id}
                  apiKey={k}
                  superadmin
                  checking={checkingId === k.id}
                  onCheck={check}
                  onEdit={(key) => {
                    setKeyError(null);
                    setKeyForm({
                      orgId: selected.id,
                      key,
                      name: key.name,
                      pageUrl: key.pageUrl,
                    });
                  }}
                  onRevoke={revokeKey}
                  onShowChecks={showChecks}
                  onActivate={openActivate}
                />
              ))
          )}
        </>
      )}

      <PageModal
        open={!!form}
        title={form?.id ? "Endre partner" : "Ny partner"}
        icon="link"
        width={620}
        onClose={() => setForm(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setForm(null)}>
              Avbryt
            </Button>
            <Button
              onClick={saveOrg}
              loading={saving}
              disabled={!form?.name.trim()}
            >
              Lagre
            </Button>
          </>
        }
      >
        {form && (
          <>
            {!form.id && mosques && mosques.length > 0 && (
              <Select
                label="Hent fra moské (valgfritt)"
                value=""
                onChange={(e) => fillFromMosque(e.target.value)}
                options={[
                  {
                    value: "",
                    label: "Velg moské for å fylle inn navn og org.nr.",
                  },
                  ...mosques.map((m) => ({
                    value: m.orgNr,
                    label: `${m.name || m.orgName} (${m.orgNr})`,
                  })),
                ]}
                help="Når en moské vil ha widgeten på nettsiden sin."
              />
            )}
            <TextField
              label="Navn"
              value={form.name}
              onChange={(e) => set({ name: e.target.value })}
              autoFocus
            />
            <TextField
              label="Org.nr."
              help="Vises bare for superadmin."
              value={form.orgNr}
              onChange={(e) => set({ orgNr: e.target.value })}
            />
            <TextField
              label="Kontaktperson"
              value={form.contactName}
              onChange={(e) => set({ contactName: e.target.value })}
            />
            <TextField
              label="E-post til kontakt"
              type="email"
              value={form.contactEmail}
              onChange={(e) => set({ contactEmail: e.target.value })}
            />
            <Select
              label="Status"
              value={form.status}
              onChange={(e) => set({ status: e.target.value as PartnerStatus })}
              options={(Object.keys(partnerStatusLabel) as PartnerStatus[]).map(
                (s) => ({
                  value: s,
                  label: partnerStatusLabel[s],
                }),
              )}
              help="Satt på pause: widgeten viser en pausemelding. Avsluttet: widgeten og nøklene slutter å virke."
            />
            <TextField
              label="Maks aktive nøkler"
              type="number"
              min={1}
              max={50}
              value={form.maxKeys}
              onChange={(e) => set({ maxKeys: e.target.value })}
            />
            <Textarea
              label="Notat (bare superadmin)"
              value={form.note}
              onChange={(e) => set({ note: e.target.value })}
            />
            {formError && <Alert tone="error">{formError}</Alert>}
          </>
        )}
      </PageModal>

      <PageModal
        open={!!keyForm}
        title={
          keyForm?.key
            ? "Endre side"
            : `Ny nøkkel · ${orgs?.find((o) => o.id === keyForm?.orgId)?.name ?? ""}`
        }
        icon="key"
        width={560}
        onClose={() => setKeyForm(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setKeyForm(null)}>
              Avbryt
            </Button>
            <Button
              onClick={saveKey}
              loading={saving}
              disabled={!keyForm?.pageUrl.trim()}
            >
              {keyForm?.key ? "Lagre" : "Lag nøkkel"}
            </Button>
          </>
        }
      >
        {keyForm && (
          <>
            <TextField
              label="Adressen til siden widgeten skal stå på"
              placeholder="https://foreningen.no/bonnetider"
              value={keyForm.pageUrl}
              onChange={(e) =>
                setKeyForm((f) => (f ? { ...f, pageUrl: e.target.value } : f))
              }
              help={
                keyForm.key
                  ? `Siden må ligge på ${keyForm.key.domain}. Et annet nettsted trenger en ny nøkkel.`
                  : "Nøkkelen låses til dette nettstedet (med og uten www)."
              }
              autoFocus
            />
            <TextField
              label="Navn (valgfritt)"
              placeholder="F.eks. Nettsiden"
              value={keyForm.name}
              onChange={(e) =>
                setKeyForm((f) => (f ? { ...f, name: e.target.value } : f))
              }
              maxLength={120}
            />
            {keyError && <Alert tone="error">{keyError}</Alert>}
          </>
        )}
      </PageModal>

      <PageModal
        open={!!activateForm}
        title={`${activateForm?.key.pausedAt ? "Aktiver" : "Endre frist"} · ${activateForm?.key.domain ?? ""}`}
        icon="check"
        width={520}
        onClose={() => setActivateForm(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setActivateForm(null)}>
              Avbryt
            </Button>
            <Button
              onClick={activate}
              loading={saving}
              disabled={
                !activateForm?.graceUntil ||
                activateForm.graceUntil < isoDayFromToday(0)
              }
            >
              {activateForm?.key.pausedAt ? "Aktiver" : "Lagre"}
            </Button>
          </>
        }
      >
        {activateForm && (
          <>
            <TextField
              label="Siste dag de kan bruke widgeten om kontrollen feiler"
              type="date"
              min={isoDayFromToday(0)}
              max={isoDayFromToday(366)}
              value={activateForm.graceUntil}
              onChange={(e) =>
                setActivateForm((f) =>
                  f ? { ...f, graceUntil: e.target.value } : f,
                )
              }
              help="Widgeten virker igjen med en gang og til og med denne dagen, selv om nattsjekken ikke finner widgeten eller lenken. Krediterer siden fortsatt ikke iBønn, stoppes den natten etter."
              autoFocus
            />
            {activateError && <Alert tone="error">{activateError}</Alert>}
          </>
        )}
      </PageModal>

      <PageModal
        open={!!history}
        title={`Krediteringssjekker · ${history?.key.domain ?? ""}`}
        icon="check"
        width={980}
        onClose={() => setHistory(null)}
        footer={
          <Button variant="secondary" onClick={() => setHistory(null)}>
            Lukk
          </Button>
        }
      >
        {history?.rows === null ? (
          <Skeleton height={120} />
        ) : (
          <Table
            columns={historyColumns}
            rows={history?.rows ?? []}
            rowKey={(c) => c.id}
            compact
            empty={<EmptyState icon="check" title="Ikke sjekket ennå" />}
          />
        )}
      </PageModal>
    </>
  );
};

export default PartnersPage;
